/**
 * Friction Priorities: top 10 questions by adjusted friction; user selects 2–4 to address on the Bridge Card.
 * Shown after Entropy Heat Map when Local Bridge API is enabled.
 */
import React, { useState, useEffect } from 'react';
import { Card, Checkbox, Button, Spin, message, Modal, Typography } from 'antd';
import { useParams } from 'react-router-dom';
import { useBridgeSession } from '../../contexts/BridgeSessionContext';
import {
  getTopFriction,
  getBridgeCard,
  setBridgeCard,
  type FrictionItemWithQuestion,
} from '../../services/localBridgeSessionApi';
import {
  getProductDomainFriction,
  getProductDomainBridgeCard,
  setProductDomainBridgeCard,
} from '../../services/productDomainSessionApi';
import styles from './FrictionPriorities.module.css';

/** Returns true if any bridge card item has Define Conditions data (future condition, assumption, plank, etc.). */
function hasConditionDetails(items: Array<Record<string, unknown>>): boolean {
  return items.some(
    (item) =>
      (item.future_condition != null && String(item.future_condition).trim() !== '') ||
      (item.assumption_text != null && String(item.assumption_text).trim() !== '') ||
      (item.plank_description != null && String(item.plank_description).trim() !== '') ||
      (item.resource_assignment != null && String(item.resource_assignment).trim() !== '') ||
      (item.estimated_duration != null && String(item.estimated_duration).trim() !== '') ||
      (item.future_answer_score != null)
  );
}

const MIN_SELECT = 2;
const MAX_SELECT = 4;

const { Text } = Typography;

export const FrictionPriorities: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { sessionType, navigateToStep } = useBridgeSession();
  const [items, setItems] = useState<FrictionItemWithQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    setLoading(true);
    const loadFriction = sessionType === 'product'
      ? Promise.all([getProductDomainFriction(sessionId, 10), getProductDomainBridgeCard(sessionId)])
      : Promise.all([getTopFriction(sessionId, 10), getBridgeCard(sessionId)]);
    loadFriction.then(([list, bridgeCardItems]) => {
        if (cancelled) return;
        setItems(list);
        const topIds = new Set(list.map((i) => i.question_id));
        const existingSelected = new Set(
          (bridgeCardItems as Array<{ question_id: string }>).map((b) => b.question_id).filter((id) => topIds.has(id))
        );
        setSelected(existingSelected);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [sessionId, sessionType]);

  const toggle = (questionId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(questionId)) next.delete(questionId);
      else if (next.size < MAX_SELECT) next.add(questionId);
      return next;
    });
  };

  const handleContinue = async () => {
    if (selected.size < MIN_SELECT || selected.size > MAX_SELECT) {
      message.warning(`Please select between ${MIN_SELECT} and ${MAX_SELECT} questions to address.`);
      return;
    }
    if (!sessionId) return;

    const currentCard = sessionType === 'product'
      ? await getProductDomainBridgeCard(sessionId)
      : await getBridgeCard(sessionId);
    if (currentCard.length > 0 && hasConditionDetails(currentCard as unknown as Array<Record<string, unknown>>)) {
      Modal.confirm({
        title: 'Replace Define Conditions data?',
        content:
          'Define Conditions already has data for this session. Continuing will replace the bridge card selection. Previous condition details will be kept for recovery but will no longer be shown. Do you want to continue?',
        okText: 'Continue and replace',
        cancelText: 'Cancel',
        onOk: () => applyAndNavigate(),
      });
      return;
    }
    await applyAndNavigate();
  };

  const applyAndNavigate = async () => {
    if (!sessionId) return;
    if (selected.size < MIN_SELECT || selected.size > MAX_SELECT) return;
    setSubmitting(true);
    try {
      const order = items.map((i) => i.question_id).filter((id) => selected.has(id));
      const payload = order.map((question_id, i) => ({
        question_id,
        reason: 'High friction priority',
        priority: i,
      }));
      const ok = sessionType === 'product'
        ? (await setProductDomainBridgeCard(sessionId, payload))?.ok ?? false
        : await setBridgeCard(sessionId, payload);
      if (ok) {
        message.success('Selections saved. Define conditions for each question next.');
        navigateToStep('define_conditions');
      } else {
        message.error('Failed to save selections.');
      }
    } catch {
      message.error('Failed to save selections.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spin size="large" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className={styles.wrapper}>
        <Card>
          <p>No friction data yet. Run the Questions step and Calculate first.</p>
          <Button type="primary" onClick={() => navigateToStep('entropy_heat_map')}>
            Back to Entropy Heat Map
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <Typography.Title level={2}>Prioritize questions to address</Typography.Title>
        <Text type="secondary">
          These are the top 10 questions by friction (impact). Select 2–4 to focus on in your Bridge Card.
        </Text>
      </div>

      <div className={styles.list}>
        {items.map((item) => (
          <div
            key={item.question_id}
            className={`${styles.item} ${selected.has(item.question_id) ? styles.selected : ''}`}
            onClick={() => toggle(item.question_id)}
          >
            <Checkbox
              checked={selected.has(item.question_id)}
              onChange={() => toggle(item.question_id)}
              onClick={(e) => e.stopPropagation()}
            />
            <div className={styles.itemContent}>
              <div className={styles.itemMeta}>
                {item.domain} / {item.factor} · Score {item.score} · Friction {item.adjusted_friction.toFixed(1)}
              </div>
              <div className={styles.itemText}>{item.text}</div>
            </div>
            <span className={styles.frictionBadge}>
              {item.adjusted_friction.toFixed(1)}
            </span>
          </div>
        ))}
      </div>

      <div className={styles.actions}>
        <Button
          type="primary"
          size="large"
          onClick={handleContinue}
          disabled={selected.size < MIN_SELECT || selected.size > MAX_SELECT}
          loading={submitting}
        >
          Continue to Define conditions ({selected.size} selected)
        </Button>
        <span className={styles.hint}>
          Select between {MIN_SELECT} and {MAX_SELECT} questions.
        </span>
      </div>
    </div>
  );
};

export default FrictionPriorities;
